import { CloseCircleFilled, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Popconfirm, Tag, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { partnerApi } from "api/partner.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  CodeCell,
  TableLayout,
  TableLayoutFooter,
  TableContent,
  getTableShowingRange,
} from "components/table";
import { fetchStaffOptions, useRemoteOptions } from "hooks/useRemoteOptions";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { userStore } from "store/userStore";
import { Partner } from "types/partner";
import { QueryParam } from "types/query";
import { getTitle } from "utils";
import { can } from "utils/auth";
import dayjs from "utils/dayjs";
import { customerIndustryOptions } from "views/Customer/customer.meta";
import { formatMoney } from "views/Opportunity/opportunity.meta";
import { PartnerModal } from "./components/PartnerModal";
import {
  getPartnerRelation,
  getPartnerStatus,
  getPartnerTypeLabel,
  PARTNER_PATH,
  partnerRelationOptions,
  partnerStatusOptions,
} from "./partner.meta";

export const PartnerPage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<QueryParam>({ page: 1, limit: 50, search: "" });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Partner[]>([]);
  const [total, setTotal] = useState(0);
  const partnerModalRef = useRef<PartnerModal>(null);

  const onlyMine = !userStore.info.role?.isAdmin && can(PARTNER_PATH, "ONLY_MINE");
  const staffFilter = useRemoteOptions(fetchStaffOptions, !onlyMine);

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const fetchData = useCallback(async (queryParams: QueryParam) => {
    setLoading(true);
    try {
      const res = await partnerApi.findAll(queryParams);
      setData(res.data.partners || []);
      setTotal(res.data.total || 0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(query);
  }, [fetchData, query]);

  const handleDelete = useCallback(
    async (partner: Partner) => {
      setLoading(true);
      try {
        await partnerApi.delete(partner.id);
        message.success("Xóa đối tác thành công");
        await fetchData(query);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, query],
  );

  const debounceSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        setQuery((prev) => ({ ...prev, search: keyword, page: 1 }));
      }, 300),
    [],
  );

  useEffect(() => () => debounceSearch.cancel(), [debounceSearch]);

  const stopRowClick = useCallback((event: React.MouseEvent) => event.stopPropagation(), []);
  const openView = useCallback((partner: Partner) => partnerModalRef.current?.handleView(partner), []);
  const openEdit = useCallback((partner: Partner) => partnerModalRef.current?.handleUpdate(partner), []);
  const openCreate = useCallback(() => partnerModalRef.current?.handleCreate(), []);
  const refresh = useCallback(() => fetchData(query), [fetchData, query]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      debounceSearch(value);
    },
    [debounceSearch],
  );

  const handleClearSearch = useCallback(() => {
    setSearch("");
    debounceSearch.cancel();
    setQuery((prev) => ({ ...prev, page: 1, search: "" }));
  }, [debounceSearch]);

  const handlePageChange = useCallback(({ limit, page }: { limit: number; page: number }) => {
    setQuery((prev) => ({ ...prev, page, limit }));
  }, []);

  const setQueryFilter = useCallback((key: string, value?: string | number) => {
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (value) {
        next[key] = value;
      } else {
        delete next[key];
      }
      return next;
    });
  }, []);

  const filters = useMemo(
    () => [
      {
        key: "industry",
        value: (query.industry as string | undefined) ?? "",
        options: customerIndustryOptions,
        onChange: (value: string | number | undefined) => setQueryFilter("industry", value ? String(value) : undefined),
        placeholder: "Nhóm ngành nghề",
        allowClear: true,
        width: 190,
      },
      {
        key: "relationLevel",
        value: (query.relationLevel as string | undefined) ?? "",
        options: partnerRelationOptions,
        onChange: (value: string | number | undefined) => setQueryFilter("relationLevel", value ? String(value) : undefined),
        placeholder: "Mức quan hệ",
        allowClear: true,
        width: 160,
      },
      {
        key: "status",
        value: (query.status as string | undefined) ?? "",
        options: partnerStatusOptions,
        onChange: (value: string | number | undefined) => setQueryFilter("status", value ? String(value) : undefined),
        placeholder: "Trạng thái",
        allowClear: true,
        width: 160,
      },
      ...(onlyMine
        ? []
        : [
          {
            key: "staffId",
            value: (query.staffId as number | undefined) ?? "",
            options: staffFilter.options,
            onChange: (value: string | number | undefined) => {
              const staffId = typeof value === "number" ? value : undefined;
              staffFilter.select(staffId);
              setQueryFilter("staffId", staffId);
            },
            placeholder: "Sale phụ trách",
            allowClear: true,
            showSearch: true,
            filterOption: false as const,
            onSearch: staffFilter.onSearch,
            width: 200,
          },
        ]),
    ],
    [onlyMine, query.industry, query.relationLevel, query.staffId, query.status, setQueryFilter, staffFilter],
  );

  const canCreate = can(PARTNER_PATH, "CREATE");
  const canEdit = can(PARTNER_PATH, "UPDATE");
  const canDelete = can(PARTNER_PATH, "DELETE");

  const columns = useMemo<TableColumnsType<Partner>>(
    () => [
      { title: "Mã", dataIndex: "code", key: "code", width: 110, render: (value) => <CodeCell value={value} /> },
      {
        title: "Tên",
        key: "name",
        render: (_, record) => (
          <div>
            <div className="font-semibold">{record.name || "--"}</div>
            {record.company && <div className="text-xs text-gray-500">{record.company}</div>}
          </div>
        ),
      },
      {
        title: "Loại",
        dataIndex: "type",
        key: "type",
        width: 100,
        render: (value) => <TableContent>{getPartnerTypeLabel(value)}</TableContent>,
      },
      {
        title: "Nhóm ngành nghề",
        dataIndex: "industry",
        key: "industry",
        width: 150,
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Sale phụ trách",
        key: "staff",
        width: 160,
        render: (_, record) => <TableContent>{record.staff?.name || "--"}</TableContent>,
      },
      {
        title: "Mức quan hệ",
        dataIndex: "relationLevel",
        key: "relationLevel",
        width: 130,
        render: (value) => {
          const relation = getPartnerRelation(value);
          return relation ? <Tag color={relation.color}>{relation.label}</Tag> : <TableContent>--</TableContent>;
        },
      },
      {
        title: "Số khách",
        dataIndex: "customerCount",
        key: "customerCount",
        width: 100,
        align: "right" as const,
        render: (value) => <TableContent>{value || 0}</TableContent>,
      },
      {
        title: "Doanh thu mang về",
        dataIndex: "totalAmount",
        key: "totalAmount",
        width: 170,
        align: "right" as const,
        render: (value) => <TableContent>{formatMoney(value || 0)}</TableContent>,
      },
      {
        title: "Liên hệ gần nhất",
        dataIndex: "lastContactDate",
        key: "lastContactDate",
        width: 140,
        render: (value) => <TableContent>{value ? dayjs(value).format("DD/MM/YYYY") : "--"}</TableContent>,
      },
      {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
        width: 130,
        render: (value) => {
          const status = getPartnerStatus(value);
          return status ? <Tag color={status.color}>{status.label}</Tag> : <TableContent>--</TableContent>;
        },
      },
      ...(canEdit || canDelete
        ? [
          {
            title: "Thao tác",
            key: "action",
            width: 100,
            align: "center" as const,
            render: (_: unknown, record: Partner) => (
              <div className="flex gap-2 justify-center items-center" onClick={stopRowClick}>
                {canEdit && (
                  <Tooltip title="Cập nhật">
                    <ActionButton aria-label="Cập nhật" onClick={() => openEdit(record)}>
                      <EditOutlined style={{ color: "#6c84e5" }} />
                    </ActionButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Xóa">
                    <Popconfirm
                      cancelText="Không"
                      okText="Xóa"
                      onConfirm={() => handleDelete(record)}
                      title="Xóa đối tác này? Các khách do đối tác giới thiệu sẽ không còn gắn đối tác."
                    >
                      <ActionButton aria-label="Xóa">
                        <DeleteOutlined style={{ color: "#ef4444" }} />
                      </ActionButton>
                    </Popconfirm>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]
        : []),
    ],
    [canDelete, canEdit, handleDelete, openEdit, stopRowClick],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              width: 420,
              placeholder: "Tìm theo tên, mã, công ty, liên hệ, hợp tác",
              onChange: handleSearchChange,
              inputProps: {
                allowClear: {
                  clearIcon: <CloseCircleFilled onClick={handleClearSearch} />,
                },
              },
            }}
            leftActions={
              canCreate ? (
                <AppButton tone="primary" icon={<PlusOutlined />} onClick={openCreate}>
                  Tạo mới
                </AppButton>
              ) : null
            }
            filterLabel={null}
            filters={filters}
          />
        }
        table={
          <AppTable<Partner>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
            onRow={(record) => ({ onClick: () => openView(record) })}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={handlePageChange}
          />
        }
      />

      <PartnerModal ref={partnerModalRef} onSubmitOk={refresh} onClose={() => undefined} />
    </div>
  );
});
